import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc.js';
import timezone from 'dayjs/plugin/timezone.js';

dayjs.extend(utc);
dayjs.extend(timezone);

const MINUTES_PER_ROW = 15;
const LAST_MINUTE_OF_DAY = 23 * 60 + 59;

/**
 * Which calendar rows a "Find a Session" gap block should occupy.
 *
 * The grid is one row per 15 minutes. A SESSION's span is ceiled, so a session
 * ending at 4:35 still fills the whole 4:30-4:45 row. The gap that follows it
 * therefore has to begin at the NEXT row boundary: flooring its start put it in
 * the row the session still occupies, and because the block is absolutely
 * positioned across every location column, it covered that card — including its
 * remove button (UNB-629).
 *
 * So: start is ceiled to the first genuinely free row, end is floored to the row
 * where the following session begins. Both are computed as absolute row offsets
 * from the same base rather than start-plus-duration, so the two ends can't
 * drift apart.
 *
 * Returns row OFFSETS from `baseStartMinutes`; the caller adds its own header
 * offset to get real grid-row numbers.
 */
export function gapRowRange(startValue, endValue, baseStartMinutes, tz) {
    const start = dayjs(startValue).tz(tz);
    const end = dayjs(endValue).tz(tz);

    const rowOffset = (t) => {
        const minutes = t.hour() * 60 + t.minute();
        return Math.max(0, minutes - baseStartMinutes) / MINUTES_PER_ROW;
    };

    const startRow = Math.ceil(rowOffset(start));

    // A gap running past midnight stops at the end of the day rather than
    // wrapping into negative rows.
    const endRow = end.isSame(start, 'day')
        ? Math.floor(rowOffset(end))
        : Math.floor((LAST_MINUTE_OF_DAY - baseStartMinutes) / MINUTES_PER_ROW);

    return { startRow, span: Math.max(1, endRow - startRow) };
}
